import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Search, Filter, Columns, GripVertical } from 'lucide-react';
import axios from 'axios';
import EditEntryPanel from '../components/EditEntryPanel';
import NotesModal from '../components/NotesModal';
import { useAuth } from '../contexts/AuthContext';
import { SelectOrgPrompt, useNeedsOrg } from '../components/SelectOrgPrompt';

import { BACKEND_URL, API_BASE as API } from '../config';

const availableColumns = [
  { id: 'report_no', label: 'Report No', default: true },
  { id: 'company_name', label: 'Company', default: true },
  { id: 'date_of_loss', label: 'Date Of Loss', default: true },
  { id: 'loss_types', label: 'Loss Types', default: true },
  { id: 'fault', label: 'Fault', default: true },
  { id: 'drivers_name', label: 'Driver Name', default: true },
  { id: 'drivers_ph_no', label: 'Driver Phone', default: false },
  { id: 'truck_no', label: 'Truck #', default: true },
  { id: 'trailer_no', label: 'Trailer #', default: true },
  { id: 'location', label: 'Location', default: false },
  { id: 'description', label: 'Description', default: false },
  { id: 'police_involved', label: 'Police Involved', default: false },
  { id: 'other_vehicle_involved', label: 'Other Vehicle', default: false },
  { id: 'tow_away', label: 'Tow Away', default: false },
  { id: 'injury', label: 'Injury', default: false },
  { id: 'fatality', label: 'Fatality', default: false },
  { id: 'status', label: 'Status', default: true },
  { id: 'last_contact', label: 'Last Contact', default: true },
  { id: 'created_at', label: 'Created Date', default: false },
];

const getLossTypes = (claim) => {
  const types = [];
  if (claim.al) types.push('AL');
  if (claim.pd) types.push('PD');
  if (claim.cd) types.push('CD');
  if (claim.ocac) types.push('OCAC');
  if (claim.wc) types.push('WC');
  return types;
};

const LOSS_KEYS = ['al', 'pd', 'cd', 'ocac', 'wc'];

/** Render the "Last Contact" pill — a colour-coded indicator of how recently
 *  someone touched this claim (email > note > customer update > edit). Stale
 *  claims jump out so users naturally work the oldest items first. */
const LAST_CONTACT_LABELS = { email: 'Email', note: 'Note', update: 'Update', edit: 'Edit' };

const LastContactPill = ({ claim }) => {
  const ts = claim?.last_contact_at;
  if (!ts) {
    return <span className="text-xs text-gray-400 italic" data-testid="last-contact-none">—</span>;
  }
  const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86_400_000);
  // 0-13 days: green/teal · 14-29: amber · 30+: red. Mirrors the colour rules
  // we set for the "Last update" pill so the table reads consistently.
  let tone;
  if (days <= 13) tone = 'bg-emerald-100 text-emerald-700';
  else if (days <= 29) tone = 'bg-amber-100 text-amber-700';
  else tone = 'bg-red-100 text-red-700';
  const label = days === 0 ? 'today' : days === 1 ? '1 day' : `${days} days`;
  const kind = LAST_CONTACT_LABELS[claim.last_contact_kind] || 'Activity';
  return (
    <span
      title={`Last ${kind.toLowerCase()}: ${new Date(ts).toLocaleString()}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${tone}`}
      data-testid={`last-contact-pill-${days <= 13 ? 'fresh' : days <= 29 ? 'warn' : 'stale'}`}
    >
      <span className="text-[10px] opacity-70">{kind}</span>
      <span>{label}</span>
    </span>
  );
};

const getEntryStatus = (claim) => {
  const td = claim.type_data || {};
  const activeStatuses = LOSS_KEYS
    .filter(k => claim[k])
    .map(k => td[k]?.status || claim.status || 'New');
  if (activeStatuses.length === 0) return claim.status || 'New';
  if (activeStatuses.every(s => s === 'Closed')) return 'Closed';
  if (activeStatuses.some(s => s === 'In Process')) return 'In Process';
  return 'New';
};

const getStatusColor = (status) => {
  switch (status) {
    case 'New': return 'bg-blue-100 text-blue-700';
    case 'In Process': return 'bg-orange-100 text-orange-700';
    case 'Closed': return 'bg-green-100 text-green-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { timeZone: 'UTC' });
};

const AllEntries = () => {
  const { selectedTenants, operatorContext, activeOrgId } = useAuth();
  const needsOrg = useNeedsOrg();
  const isOperatorInContext = operatorContext?.tenantId;
  const [claims, setClaims] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  // Loss-type chip → opens the Notes modal directly for that loss type. This
  // makes the email-from-Notes feature one click away from the table instead
  // of buried inside the edit panel.
  const [notesClaim, setNotesClaim] = useState(null);
  const [notesLossType, setNotesLossType] = useState(null);
  // When the inbox / a toast deep-links here, we may want to force-open a
  // specific tab inside the NotesModal (e.g. drafts). null falls back to
  // the existing emails/comments heuristic below.
  const [notesForcedTab, setNotesForcedTab] = useState(null);
  const [columnOrder, setColumnOrder] = useState(
    availableColumns.filter(col => col.default).map(col => col.id)
  );
  const [dragIdx, setDragIdx] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [claimsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/claims`),
        axios.get(`${API}/companies`),
      ]);
      setClaims(claimsRes.data);
      setCompanies(companiesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, selectedTenants, activeOrgId, operatorContext?.tenantId, operatorContext?.orgId]);

  // Deep-link from real-time notifications: clicking "Open" on the
  // email.received toast routes here with ?open_emails=<report_no>. Once
  // the claims list has loaded we auto-open the matching NotesModal on
  // the Emails tab so users land directly on the new reply.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get('open_emails');
    if (!target || !claims || claims.length === 0) return;
    const match = claims.find((c) => c.report_no === target);
    if (match) {
      setNotesClaim(match);
      // Honour explicit loss_type from the URL (the inbox passes this so a
      // resumed draft opens on the right loss-type tab). Falls back to
      // null so the NotesModal-default behaviour kicks in.
      setNotesLossType(searchParams.get('loss_type') || null);
      // notes_tab=drafts forces the Drafts tab. Anything else (or absent)
      // keeps the existing emails/comments default.
      const tab = searchParams.get('notes_tab');
      setNotesForcedTab(tab || null);
      // Clear the URL params so a refresh doesn't re-pop the modal indefinitely
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('open_emails');
        next.delete('loss_type');
        next.delete('notes_tab');
        return next;
      }, { replace: true });
    }
  }, [claims, searchParams, setSearchParams]);

  const toggleColumn = (columnId) => {
    setColumnOrder(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setColumnOrder(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const filteredClaims = claims.filter(claim => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      (claim.report_no || '').toLowerCase().includes(search) ||
      (claim.company_name || '').toLowerCase().includes(search) ||
      (claim.drivers_name || '').toLowerCase().includes(search);
    const matchesStatus = statusFilter === 'all' || getEntryStatus(claim) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleRowClick = (claim) => {
    setSelectedClaim(claim);
    setIsEditOpen(true);
  };

  const renderCellContent = (claim, columnId) => {
    switch (columnId) {
      case 'report_no':
        return (
          <span className="font-medium text-blue-600 flex items-center gap-1.5">
            {claim.report_no}
            {claim.assistance_requested && claim.assistance_status && claim.assistance_status !== 'canceled' && (
              <span className={`inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold ${
                claim.assistance_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                claim.assistance_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                claim.assistance_status === 'returned' ? 'bg-orange-100 text-orange-700' :
                'bg-green-100 text-green-700'
              }`} data-testid="assistance-indicator" title={`Assistance: ${claim.assistance_status}`}>
                {claim.assistance_status === 'pending' ? 'HELP' :
                 claim.assistance_status === 'in_progress' ? 'WIP' :
                 claim.assistance_status === 'returned' ? 'RTN' : 'DONE'}
              </span>
            )}
          </span>
        );
      case 'company_name':
        return claim.company_name;
      case 'date_of_loss':
        return formatDate(claim.date_of_loss);
      case 'loss_types':
        return (
          <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
            {getLossTypes(claim).map(t => {
              const key = t.toLowerCase(); // AL → al
              return (
                <button
                  key={t}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNotesClaim(claim);
                    setNotesLossType(key);
                  }}
                  className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium hover:bg-purple-200 hover:shadow-sm transition-all cursor-pointer"
                  title={`Open Notes & Emails for ${t}`}
                  data-testid={`losstype-chip-${key}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        );
      case 'fault':
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            claim.fault === 'At Fault' ? 'bg-red-100 text-red-700' :
            claim.fault === 'Not At Fault' ? 'bg-green-100 text-green-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {claim.fault || 'Unknown'}
          </span>
        );
      case 'drivers_name':
        return claim.drivers_name || '-';
      case 'drivers_ph_no':
        return claim.drivers_ph_no || '-';
      case 'truck_no':
        return claim.truck_no || '-';
      case 'trailer_no':
        return claim.trailer_no || '-';
      case 'location':
        return <div className="max-w-xs truncate" title={claim.location}>{claim.location || '-'}</div>;
      case 'description':
        return <div className="max-w-xs truncate" title={claim.description}>{claim.description || '-'}</div>;
      case 'police_involved':
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            claim.police_involved ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {claim.police_involved ? 'Yes' : 'No'}
          </span>
        );
      case 'other_vehicle_involved':
        return claim.other_vehicle_involved || '-';
      case 'tow_away':
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            claim.tow_away ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {claim.tow_away ? 'Yes' : 'No'}
          </span>
        );
      case 'injury':
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            claim.injury ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {claim.injury ? 'Yes' : 'No'}
          </span>
        );
      case 'fatality':
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            claim.fatality ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {claim.fatality ? 'Yes' : 'No'}
          </span>
        );
      case 'status':
        const entryStatus = getEntryStatus(claim);
        return (
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(entryStatus)}`}>
            {entryStatus}
          </span>
        );
      case 'created_at':
        return formatDate(claim.created_at);
      case 'last_contact':
        return <LastContactPill claim={claim} />;
      default:
        return '-';
    }
  };

  if (needsOrg && !isOperatorInContext) return <SelectOrgPrompt />;

  return (
    <div className="h-[calc(100vh-57px)] bg-gray-200 p-4 md:p-8 flex flex-col overflow-hidden">
      <div className="max-w-7xl mx-auto w-full flex flex-col flex-1 min-h-0">
        <Card className="border-none shadow-sm flex flex-col flex-1 min-h-0">
          <CardHeader className="px-4 md:px-6">
            <CardTitle className="text-xl md:text-2xl font-bold text-blue-500" data-testid="all-entries-title">All Entries</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 min-h-0 px-4 md:px-6">
            <div className="flex flex-col gap-3 mb-4 md:mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search by report no, company, or driver..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="text-gray-400 w-4 h-4 shrink-0" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px] md:w-[180px]" data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="In Process">In Process</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2" data-testid="columns-btn">
                      <Columns className="w-4 h-4" />
                      Columns
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="end">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm">Select Columns</h4>
                      <div className="flex gap-2">
                        <button
                          className="text-xs text-blue-500 hover:underline"
                          onClick={() => setColumnOrder(availableColumns.map(c => c.id))}
                        >
                          All
                        </button>
                        <button
                          className="text-xs text-blue-500 hover:underline"
                          onClick={() => setColumnOrder(availableColumns.filter(c => c.default).map(c => c.id))}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Drag to reorder</p>
                    <div className="space-y-1 max-h-72 overflow-y-auto">
                      {columnOrder.map((colId, idx) => {
                        const col = availableColumns.find(c => c.id === colId);
                        if (!col) return null;
                        return (
                          <div
                            key={col.id}
                            draggable
                            onDragStart={() => handleDragStart(idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDragEnd={handleDragEnd}
                            className={`flex items-center gap-2 px-1 py-1 rounded cursor-grab active:cursor-grabbing ${
                              dragIdx === idx ? 'bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <GripVertical className="w-3 h-3 text-gray-300 flex-shrink-0" />
                            <Checkbox
                              id={`col-${col.id}`}
                              checked={true}
                              onCheckedChange={() => toggleColumn(col.id)}
                              className="w-4 h-4"
                            />
                            <Label htmlFor={`col-${col.id}`} className="text-sm cursor-pointer flex-1">
                              {col.label}
                            </Label>
                          </div>
                        );
                      })}
                      {availableColumns.filter(c => !columnOrder.includes(c.id)).map(col => (
                        <div key={col.id} className="flex items-center gap-2 px-1 py-1 hover:bg-gray-50 rounded">
                          <div className="w-3" />
                          <Checkbox
                            id={`col-${col.id}`}
                            checked={false}
                            onCheckedChange={() => toggleColumn(col.id)}
                            className="w-4 h-4"
                          />
                          <Label htmlFor={`col-${col.id}`} className="text-sm cursor-pointer flex-1 text-gray-400">
                            {col.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="border rounded-lg overflow-auto flex-1 min-h-0">
              <Table className="table-auto">
                <TableHeader className="sticky top-0 z-10 bg-gray-50">
                  <TableRow className="bg-gray-50">
                    {columnOrder.map(colId => {
                      const col = availableColumns.find(c => c.id === colId);
                      return col ? (
                        <TableHead key={col.id} className="font-semibold bg-gray-50 whitespace-nowrap">
                          {col.label}
                        </TableHead>
                      ) : null;
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={columnOrder.length} className="text-center py-8 text-gray-500">
                        Loading claims...
                      </TableCell>
                    </TableRow>
                  ) : filteredClaims.length > 0 ? (
                    filteredClaims.map((claim) => (
                      <TableRow
                        key={claim.report_no}
                        className="hover:bg-blue-50 transition-colors cursor-pointer"
                        onClick={() => handleRowClick(claim)}
                        data-testid={`claim-row-${claim.report_no}`}
                      >
                        {columnOrder.map(colId => (
                          <TableCell key={colId} className="whitespace-nowrap">
                            {renderCellContent(claim, colId)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columnOrder.length} className="text-center py-8 text-gray-500">
                        No claims found matching your criteria.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 text-sm text-gray-600" data-testid="claims-count">
              Showing {filteredClaims.length} of {claims.length} claims
            </div>
          </CardContent>
        </Card>
      </div>

      <EditEntryPanel
        claim={selectedClaim}
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        companies={companies}
        onSaved={(updatedClaim) => {
          if (updatedClaim) {
            // Optimistic merge — avoids Atlas read-after-write lag that left tables stale
            // after edits until a manual refresh.
            setClaims(prev => {
              const oldRn = updatedClaim.old_report_no;
              const newRn = updatedClaim.report_no;
              const newDbId = newRn?.split('/').pop();
              const idx = prev.findIndex(c =>
                c.report_no === newRn ||
                (oldRn && c.report_no === oldRn) ||
                (newDbId && c.report_no?.endsWith('/' + newDbId))
              );
              if (idx === -1) return [updatedClaim, ...prev];
              const next = [...prev];
              next[idx] = { ...next[idx], ...updatedClaim };
              return next;
            });
            setSelectedClaim(updatedClaim);
          } else {
            // Delete or unknown change — refetch
            fetchData();
          }
        }}
      />

      {/* Direct entry to Notes & Emails — opened by clicking a Loss Type chip
       *  in the table OR by the email.received toast deep-link (?open_emails=).
       *  When the deep-link path is used, lossType will be null so the modal
       *  opens with the default loss type and the Emails tab pre-selected. */}
      <NotesModal
        open={!!notesClaim}
        onClose={() => { setNotesClaim(null); setNotesLossType(null); setNotesForcedTab(null); }}
        claim={notesClaim || {}}
        lossType={notesLossType}
        onSwitchType={(t) => setNotesLossType(t)}
        initialTab={notesForcedTab || (notesLossType ? 'comments' : 'emails')}
      />
    </div>
  );
};

export default AllEntries;
